<template>
  <main id="main-content" class="flex-display-body container py-5">
    <delete-group-confirm-modal modal-id="groups-main-delete-group-confirm"
                                :group-id="groupToDelete"
                                @deleteGroup="onConfirmDeleteGroup"
                                @cancel="groupToDelete = ''">
    </delete-group-confirm-modal>
    <group-renew-modal modal-id="group-expiration-modal" :group-ids="groupIdsToRenew"
                       @groupExpirationSelected="onExpirationSelected"
                       @cancel="groupIdsToRenew = []">
    </group-renew-modal>
    <div class="row">
      <div class="col-12">
        <div class="d-flex flex-wrap pb-4">
          <h1 class="display-4 top-margin-1">Groups Managed by {{username}}</h1>
          <div class="align-self-center ml-2 mr-auto">
            <b-btn id="refreshGroup"
                   variant="primary"
                   class="btn-sm"
                   @click.stop="syncGroups"
                   type="submit"
                   aria-label="Sync groups">
              <div style="font-size: 1.1rem">
                <font-awesome-icon icon="sync"></font-awesome-icon>
              </div>
            </b-btn>
            <b-tooltip
              id="refreshToolTip"
              target="refreshGroup"
              placement="bottom"
              role="tooltip">
              Sync Groups
            </b-tooltip>
          </div>
          <div class="align-self-center ml-lg-auto">
            <b-button
              id="btn-create-group" variant="success" to="/createGroup">
              + Create New Group
            </b-button>
          </div>
        </div>
        <table-options-filter table-id="tbl-groups"
                        :per-page="groupsPerPage"
                        @perPageChanged="onPerPageChanged"
                        @filterChanged="onFilterChanged">
        </table-options-filter>
        <div class="group-table-loading-wrapper" :class="[ loading ? 'group-table-loader' : '']">
          <p class="sr-only" v-if="loading">Content is loading...</p>
          <p class="sr-only" v-else>Content has finished loading.</p>
          <b-table show-empty
                   striped
                   stacked="md"
                   id="tbl-groups"
                   sort-by="_expirationDate"
                   :items="mappedGroupsForTable"
                   :data-size="mappedGroupsForTable.length"
                   :fields="fields"
                   :current-page="currentPage"
                   :per-page="groupsPerPage"
                   :filter="tableFilter"
                   @filtered="onFiltered"
                   :busy.sync="loading">
            <template slot="table-caption">
              {{ showPaginationMessage }}
            </template>
            <template slot="empty">
              <!--Currently, this slot won't show due to a bug in Bootstrap-Vue
              if filter is set to a function (doFilter). Defaults to emptyfiltered slot.-->
              <!--See https://github.com/bootstrap-vue/bootstrap-vue/issues/1517-->
              You are not a manager or administrator of any groups.
            </template>
            <template slot="emptyfiltered">
              No groups to show.
            </template>
            <template slot="uugid" slot-scope="row">
              <b-link class="break-words"
                      :data-uugid="row.item.uugid"
                      :to="{ name: 'GroupDashboard',params: {id: row.item.uugid}, selectedComponent: 'appGroupDetail' }"
              >
                {{row.item.uugid}}
              </b-link>
            </template>
            <template slot="_expirationDate" slot-scope="row">
              <div :id="row.item._expElementId">{{row.item._expString}}</div>
            </template>
            <template slot="actions" slot-scope="row">
              <template v-if="row.item._isAdmin || row.item._isManager">
                <!-- Show message when group is in the process of being deleted -->
                <b-btn v-if="submittingGroupDelete && groupToDelete === row.item.uugid"
                       variant="primary" id="group-deleting-notify" disabled>
                  Deleting...
                </b-btn>
                <b-btn v-else-if="submittingGroupRenew && groupIdsToRenew[0] === row.item.uugid"
                       variant="primary" id="group-renewing-notify" disabled>
                  Renewing...
                </b-btn>
                <template v-else>
                  <group-list-actions :group="row.item"
                                      :submitting-group-delete="submittingGroupDelete"
                                      :submitting-group-renew="submittingGroupRenew"
                                      @renewOptionSelected="onRenewOptionSelected"
                                      @deleteOptionSelected="onDeleteOptionSelected">
                  </group-list-actions>
                </template>
              </template>
            </template>
          </b-table>
        </div>
        <b-row>
          <b-col md="6" class="my-1">
            <b-pagination
              id="mnu-pagination" :total-rows="totalRows"
              :per-page="groupsPerPage"
              v-model="currentPage" class="my-0">
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>
  </main>
</template>

<script>
/* eslint-disable no-underscore-dangle */

import moment from 'moment';
import { eventBus } from '../main';
import { auth } from '../util/groups-mixin';
import { deleteGroup } from '../util/delete-group-mixin';
import { renewGroup } from '../util/renew-group-mixin';
import tableMixin from '../util/table-mixin';
import GroupListActions from './GroupListActions';
import NoAdministeredGroupsAlert from './NoAdministeredGroupsAlert';
import TableOptionsFilter from './table/TableOptionsFilter';


export default {
  name: 'Groups',
  title: 'Group Manager',
  mixins: [deleteGroup, renewGroup, auth, tableMixin],
  data() {
    return {
      today: moment(),
      fields: [
        {
          key: 'uugid',
          label: 'Group',
          sortable: true,
          tdClass: 'groups-name-col',
          thClass: 'tbl-header-uugid',
        },
        {
          key: '_expirationDate',
          label: 'Expires',
          sortable: true,
          tdClass: 'expirationClass',
          thClass: 'tbl-header-exp',
        },
        { key: 'actions', label: 'Actions', tdClass: 'groups-action-col', thClass: 'tbl-header-actions' },
      ],
      currentDate: new Date(),
      loading: false,
    };
  },
  methods: {
    /* Handler for event emitted from GroupListActions when the 'Renew' option is selected */
    onRenewOptionSelected(group) {
      this.groupIdsToRenew = [group.uugid];
    },
    /* Handler for event emitted from GroupListActions when the 'Delete' option is selected */
    onDeleteOptionSelected(groupId) {
      this.groupToDelete = groupId;
    },
    /* Override method from table-mixin */
    onPerPageChanged(value) {
      this.groupsPerPage = value;
    },
    /* Fetch groups from ED. Manual operation performed by the user. */
    syncGroups() {
      this.loading = true;
      this.$store.commit('clearGroups');
      this.$store.dispatch('fetchGroupListForUserFromEd').finally(() => {
        this.loading = false;
      });
    },
    /**
     * Clear groups in storage if the following conditions are met:
     *    Groups have already been fetched
     *    AND
     *    The date /groups was last accessed is before today.
     * This will force the groups to be fetched from ED to ensure the locally stored groups are in sync with ED.
     * @returns {Promise}
     */
    fetchGroupsIfPotentiallyStale() {
      const vm = this;
      return new Promise((resolve) => {
        if (vm.$store.state.groupsFetched && vm.$store.state.lastAccessed) {
          const now = moment().startOf('day');
          const lastAccessedMoment = moment(vm.$store.state.lastAccessed).startOf('day');
          if (lastAccessedMoment.isValid() && lastAccessedMoment.isBefore(now)) {
            vm.$store.commit('clearGroups');
          }
        }
        resolve('done');
      });
    },
  },
  computed: {
    /* Build a new list of groups with precomputed data that is specific to what is being rendered. This will speed
     * up rendering for a better UX.
     * NOTE: During filtering Bootstrap-Vue stringifies the objects and then searches the strings for matching values.
     *    We do not want the entire object to be matched and by convention, bootstrap-vue ignores any properties that
     *    begin with and underscore.
     * */
    mappedGroupsForTable() {
      return this.$store.getters.mappedGroupsForListing;
    },
    groupsPerPage: {
      get() {
        return this.$store.state.groupsListPerPage;
      },
      set(value) {
        this.$store.commit('setGroupsListPerPage', value);
      },
    },
    /* Table caption displaying which groups are displayed. Ex: 11 to 20 of 50 total groups */
    showPaginationMessage() {
      let from = 0;
      const totalDisplayed = this.groupsPerPage * this.currentPage;
      if (this.totalRows) {
        from = (totalDisplayed - this.groupsPerPage) + 1;
      }
      const to = totalDisplayed < this.totalRows ? totalDisplayed : this.totalRows;
      return `Showing ${Number(from)} to ${to} of ${this.totalRows} groups`;
    },
  },
  mounted() {
    const vm = this;
    vm.loading = true;
    vm.fetchGroupsIfPotentiallyStale()
      .finally(() => {
        vm.$store.dispatch('fetchGroupListForUserFromEd')
          .finally(() => {
            /* Set date that '/groups' was last accessed to today */
            vm.$store.commit('setLastAccessed', moment().startOf('day').toDate());
            vm.loading = false;
          });
      });
  },
  created() {
    eventBus.$on('groupDeleted', (response, uugid) => {
      eventBus.appStatus.statusCode = response.status;
      eventBus.appStatus.statusMessage = `${uugid} deleted successfully`;
      eventBus.displayStatus();
    });
  },
  destroyed() {
    eventBus.clearStatus();
  },
  components: {
    appNoAdministeredGroupsAlert: NoAdministeredGroupsAlert,
    GroupListActions,
    TableOptionsFilter,
  },
};

</script>

<style>
  /* Used in dynamic css classes. Ignore 'never used' warnings */
  /*noinspection CssUnusedSymbol*/
  @media (min-width: 768px) {
    .groups-name-col {
      width: 50%;
    }
    .groups-expiration-col {
      width: 30%;
    }
    .groups-action-col {
      width: 20%;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Groups.vue